import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormRow } from "@/components/form";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import {
  FloatingInput,
  OptionalFieldTags
} from "../../foreignNationals/components/ClientDetailsFields";
import {
  DialogQuestion,
  NewCaseDialogFrame
} from "../../newCase/components/NewCaseDialogFrame";
import { companyActions } from "../actions";
import { useCaseMatches, useCompanies } from "../queries";
import type { Company } from "../types";
import { sameCompany } from "../utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefills the legal name (the new-case petitioner picker). */
  initialName?: string;
  /** Hide the "link existing cases" list (the new-case picker links its own case). */
  offerCases?: boolean;
  /** The button that hands over an existing company with this name or FEIN. */
  existingLabel?: string;
  /** A new company, or the existing one the user chose instead. */
  onCreated: (company: Company) => void;
  /** The breadcrumb chip. */
  firmName?: string | null;
};

const EMPTY = { legal: "", fein: "", street: "", city: "", state: "", zip: "" };

type OptionalField = "fein" | "address";

const OPTIONAL_FIELDS: ReadonlyArray<{ key: OptionalField; label: string }> = [
  { key: "fein", label: "FEIN" },
  { key: "address", label: "Address" }
];

const useDebounced = (value: string, ms = 300) => {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setOut(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return out;
};

/** New Company in the same 640px create modal as New Case. */
export const NewCompanyDialog: FC<Props> = ({
  open,
  onOpenChange,
  initialName = "",
  offerCases = true,
  existingLabel = "Open It",
  onCreated,
  firmName
}) => {
  const legalRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState({ ...EMPTY, legal: initialName });
  const [shown, setShown] = useState<Set<OptionalField>>(new Set());
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const name = useDebounced(form.legal);
  const matches = useCaseMatches(offerCases && open ? name : "");
  const companies = useCompanies();
  const existing = sameCompany(companies.data ?? [], name, form.fein);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, legal: initialName });
      setShown(new Set());
      setPicked(null);
    }
  }, [open, initialName]);

  const cases = useMemo(() => matches.data ?? [], [matches.data]);
  const selected = useMemo(
    () =>
      picked ??
      new Set(cases.filter((c) => c.exact).map((c) => c.individual_id)),
    [picked, cases]
  );
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const set = (key: keyof typeof EMPTY) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const canSubmit = Boolean(form.legal.trim()) && !busy && !existing;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    const company = await companyActions.create({
      entities: [
        {
          legal_name: form.legal.trim(),
          fein: form.fein.trim(),
          address: {
            street: form.street.trim(),
            city: form.city.trim(),
            state: form.state.trim(),
            zip_code: form.zip.trim(),
            country: ""
          }
        }
      ],
      individual_ids: offerCases ? [...selected] : []
    });
    setBusy(false);
    if (company) {
      onOpenChange(false);
      onCreated(company);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.key !== "Enter" || event.shiftKey)
      return;
    if (event.target instanceof HTMLButtonElement) return;
    event.preventDefault();
    void submit();
  };

  const field = (
    key: keyof typeof EMPTY,
    label: string,
    {
      required = false,
      ref
    }: { required?: boolean; ref?: typeof legalRef } = {}
  ) => (
    <div className="w-full min-w-0 pt-2.5">
      <FloatingInput
        ref={ref}
        id={`new-company-${key}`}
        label={label}
        value={form[key]}
        isRequired={required}
        autoComplete="off"
        onChange={(e) => set(key)(e.currentTarget.value)}
      />
    </div>
  );

  const hidden = OPTIONAL_FIELDS.filter((f) => !shown.has(f.key));

  const linkCount = offerCases ? selected.size : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          legalRef.current?.focus();
        }}
        className="top-16 flex max-h-[calc(100vh-8rem)] translate-y-0 flex-col gap-0 overflow-hidden bg-elevated p-0 sm:max-w-160"
        data-testid="new-company-dialog"
      >
        <NewCaseDialogFrame
          title="New Company"
          firmName={firmName || null}
          progress={0}
          onCancel={() => onOpenChange(false)}
          primaryLabel={
            linkCount
              ? `Create and Link ${linkCount} ${
                  linkCount === 1 ? "Case" : "Cases"
                }`
              : "Create Company"
          }
          onPrimary={() => void submit()}
          isPrimaryDisabled={!canSubmit}
          isPrimaryLoading={busy}
          onKeyDown={onKeyDown}
        >
          <DialogQuestion
            stepNumber={1}
            title="What's the company's legal entity?"
            description="The entity that petitions for its employees. Add more entities, worksites and job descriptions from the company page."
          >
            <div className="mt-1 flex flex-col gap-4">
              <FormRow>
                {field("legal", "Legal Name", {
                  required: true,
                  ref: legalRef
                })}
                {shown.has("fein") ? field("fein", "FEIN") : null}
              </FormRow>
              {shown.has("address") ? (
                <>
                  {field("street", "Street Address")}
                  <div className="grid grid-cols-[minmax(0,1fr)_96px_112px] gap-4">
                    {field("city", "City")}
                    {field("state", "State")}
                    {field("zip", "ZIP")}
                  </div>
                </>
              ) : null}
              {existing ? (
                <div
                  className="flex items-center gap-3 rounded-lg border border-warn-line bg-warn-soft py-2 pr-2 pl-3 text-base text-warn"
                  data-testid="company-exists"
                >
                  <span className="min-w-0 flex-1">
                    {existing.name} is already a company. Add entities and
                    worksites there.
                  </span>
                  <Button
                    size="xs"
                    variant="secondary"
                    onClick={() => {
                      onOpenChange(false);
                      onCreated(existing);
                    }}
                  >
                    {existingLabel}
                  </Button>
                </div>
              ) : offerCases && name.trim().length >= 3 ? (
                <div
                  className="flex flex-col gap-1.5"
                  data-testid="case-matches"
                >
                  <span className="text-sm text-fg-3">
                    Existing Cases With This Employer
                  </span>
                  {matches.isLoading ? (
                    <Skeleton className="h-16" />
                  ) : cases.length ? (
                    <div className="flex max-h-44 flex-col divide-y divide-line-1 overflow-y-auto rounded-lg ring-1 ring-line-2">
                      {cases.map((c) => (
                        <label
                          key={c.individual_id}
                          htmlFor={`new-link-${c.individual_id}`}
                          className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-base hover:bg-fill-1"
                        >
                          <Checkbox
                            id={`new-link-${c.individual_id}`}
                            checked={selected.has(c.individual_id)}
                            onCheckedChange={() => toggle(c.individual_id)}
                            aria-label={`Link ${c.name}`}
                          />
                          <span className="min-w-0 flex-1 truncate text-fg">
                            {c.name}
                            {c.visa ? (
                              <span className="text-fg-3"> · {c.visa}</span>
                            ) : null}
                          </span>
                          <span className="truncate text-sm text-fg-3">
                            {c.employer}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <span className="text-base text-fg-3">
                      No unlinked cases name this employer.
                    </span>
                  )}
                </div>
              ) : null}
              {hidden.length > 0 ? (
                <OptionalFieldTags
                  fields={hidden}
                  onAdd={(key) => setShown((prev) => new Set(prev).add(key))}
                />
              ) : null}
            </div>
          </DialogQuestion>
        </NewCaseDialogFrame>
      </DialogContent>
    </Dialog>
  );
};
